import { useCallback, useEffect, useId, useRef, useState } from "react";
import { BACKEND_API_BASE } from "../env";
import { AiExamples } from "./AiExamples";
import "./ai-showcase.css";

interface AiConnectDialogProps {
  onClose: () => void;
  onBack: () => void;
  initialStep?: 1 | 2;
}

type ConfigHost = "copilot-app" | "copilot-cli";
type CopyStatus = "copied" | "error";

type CopyTarget = ConfigHost | "server-name";
type CopyStates = Record<CopyTarget, CopyStatus | null>;

const configMethods = [
  { host: "copilot-app", label: "Use Copilot app" },
  { host: "copilot-cli", label: "Edit config file" },
] as const;

/**
 * Shared configuration and creation/editing examples (ux-rules § 15g).
 *
 * Deliberately separate from ShareWithAiDialog: that dialog answers *which
 * drawing*, this one answers *which assistant*. Shipping only the first left a
 * dead end — the product handed out a code and never said what could accept one.
 *
 * The server address is fetched from GET /api/health rather than baked in at
 * build time. Only the backend knows MCP_RESOURCE_ORIGIN, and the address is
 * NOT the app origin (Azure SWA proxies /api/* but not /.well-known/*), so a
 * guessed or duplicated value would produce a config that fails at discovery.
 */
export default function AiConnectDialog({
  onClose,
  onBack,
  initialStep = 1,
}: AiConnectDialogProps) {
  const [step, setStep] = useState<1 | 2>(initialStep);
  const [method, setMethod] = useState<ConfigHost>("copilot-app");
  const methodTabs = useRef<Array<HTMLButtonElement | null>>([]);
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [mcpUrl, setMcpUrl] = useState<string | null>(null);
  const [urlState, setUrlState] = useState<"loading" | "ready" | "error">("loading");
  const [copyStates, setCopyStates] = useState<CopyStates>({
    "copilot-app": null,
    "copilot-cli": null,
    "server-name": null,
  });
  const appCopyState = copyStates["copilot-app"];
  const cliCopyState = copyStates["copilot-cli"];
  const nameCopyState = copyStates["server-name"];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [handleKeyDown]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(`${BACKEND_API_BASE}/api/health`);
        if (!response.ok) throw new Error("unavailable");
        const body = (await response.json()) as { mcpUrl?: unknown };
        if (cancelled) return;
        if (typeof body.mcpUrl === "string" && body.mcpUrl) {
          setMcpUrl(body.mcpUrl);
          setUrlState("ready");
        } else {
          setUrlState("error");
        }
      } catch {
        if (!cancelled) setUrlState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (appCopyState !== "copied") return;
    const timer = setTimeout(
      () => setCopyStates((current) => ({ ...current, "copilot-app": null })),
      2000,
    );
    return () => clearTimeout(timer);
  }, [appCopyState]);

  useEffect(() => {
    if (cliCopyState !== "copied") return;
    const timer = setTimeout(
      () => setCopyStates((current) => ({ ...current, "copilot-cli": null })),
      2000,
    );
    return () => clearTimeout(timer);
  }, [cliCopyState]);

  useEffect(() => {
    if (nameCopyState !== "copied") return;
    const timer = setTimeout(
      () => setCopyStates((current) => ({ ...current, "server-name": null })),
      2000,
    );
    return () => clearTimeout(timer);
  }, [nameCopyState]);

  const copilotCliSnippet = mcpUrl
    ? `{
  "mcpServers": {
    "excalidraw-ms": {
      "type": "http",
      "url": "${mcpUrl}",
      "tools": ["*"]
    }
  }
}`
    : null;

  const handleCopy = async (host: CopyTarget, snippet: string | null) => {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopyStates((current) => ({ ...current, [host]: "copied" }));
    } catch {
      setCopyStates((current) => ({ ...current, [host]: "error" }));
    }
  };

  const copyLabel = (host: CopyTarget, defaultLabel: string) => {
    if (copyStates[host] === null) return defaultLabel;
    return copyStates[host] === "copied" ? "Copied!" : "Retry";
  };

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
    >
      <div
        style={dialogStyle}
        onClick={(event) => event.stopPropagation()}
        data-testid="ai-connect-dialog"
        ref={panel}
        tabIndex={-1}
      >
        <div className="ai-setup-header">
          <button
            className="ai-step-nav"
            aria-label="Back"
            title="Back to Create with AI"
            onClick={onBack}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m14 7-5 5 5 5" />
            </svg>
          </button>
          <h2 id={titleId} style={titleStyle}>
            {step === 2 ? "Create and edit drawings" : "Set up GitHub Copilot"}
          </h2>
        </div>
        <ol className="ai-stepper" aria-label="Setup progress">
          {(
            [
              { label: "Configure MCP", value: 1 },
              { label: "Create & edit", value: 2 },
            ] as const
          ).map(({ label, value }) => (
            <li key={value}>
              <button
                type="button"
                aria-label={value + ". " + label}
                aria-current={step === value ? "step" : undefined}
                onClick={() => setStep(value)}
              >
                <span className="ai-stepper-number">{value}</span>
                <span className="ai-stepper-label">{label}</span>
              </button>
            </li>
          ))}
        </ol>
        <div className="ai-guided-flow">
          {step === 1 && (
            <section style={itemStyle}>
              <p id={`${titleId}-method-hint`} className="ai-example-instruction">
                Choose either setup option below.
              </p>
              <div
                className="ai-example-tabs"
                role="tablist"
                aria-label="MCP setup method"
                aria-describedby={`${titleId}-method-hint`}
              >
                {configMethods.map(({ host, label }, index) => (
                  <button
                    key={host}
                    ref={(element) => {
                      methodTabs.current[index] = element;
                    }}
                    type="button"
                    role="tab"
                    id={`${titleId}-${host}-tab`}
                    aria-controls={`${titleId}-${host}-panel`}
                    aria-selected={method === host}
                    tabIndex={method === host ? 0 : -1}
                    onClick={() => setMethod(host)}
                    onKeyDown={(event) => {
                      let next: number;
                      if (event.key === "ArrowRight") next = (index + 1) % configMethods.length;
                      else if (event.key === "ArrowLeft")
                        next = (index + configMethods.length - 1) % configMethods.length;
                      else if (event.key === "Home") next = 0;
                      else if (event.key === "End") next = configMethods.length - 1;
                      else return;
                      event.preventDefault();
                      setMethod(configMethods[next].host);
                      methodTabs.current[next]?.focus();
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {urlState === "loading" && (
                <p style={mutedStyle} data-testid="ai-connect-url-loading">
                  Loading the server address…
                </p>
              )}

              {urlState === "error" && (
                <p style={warningStyle} role="status" data-testid="ai-connect-url-error">
                  The server address could not be loaded, so the settings below are not shown. Try
                  again in a moment, or see the full guide for the current address.
                </p>
              )}

              <div
                id={`${titleId}-copilot-app-panel`}
                role="tabpanel"
                aria-labelledby={`${titleId}-copilot-app-tab`}
                hidden={method !== "copilot-app"}
                tabIndex={0}
              >
                <strong>In the GitHub Copilot app, add a custom MCP server.</strong>
                <dl className="ai-config-fields">
                  <dt>Server name</dt>
                  <dd className="ai-config-copy-value">
                    <code style={codeStyle}>excalidraw-ms</code>
                    <button
                      type="button"
                      className="ai-text-link"
                      onClick={() => void handleCopy("server-name", "excalidraw-ms")}
                      aria-live="polite"
                    >
                      {copyLabel("server-name", "Copy server name")}
                    </button>
                  </dd>
                  <dt>Server type</dt>
                  <dd>HTTP</dd>
                  {urlState === "ready" && mcpUrl && (
                    <>
                      <dt>URL</dt>
                      <dd className="ai-config-copy-value">
                        <code style={codeStyle}>{mcpUrl}</code>
                        <button
                          type="button"
                          className="ai-text-link"
                          onClick={() => void handleCopy("copilot-app", mcpUrl)}
                          aria-live="polite"
                        >
                          {copyLabel("copilot-app", "Copy URL")}
                        </button>
                      </dd>
                    </>
                  )}
                </dl>
                {nameCopyState === "error" && (
                  <p role="alert" style={warningStyle}>
                    Copy failed. Try again or select and copy the server name.
                  </p>
                )}
                {copyStates["copilot-app"] === "error" && (
                  <p role="alert" style={warningStyle}>
                    Copy failed. Try again or select and copy the URL.
                  </p>
                )}
                <p style={mutedStyle}>
                  Leave optional headers and OAuth Client ID blank, keep the default timeout, and
                  select <strong>Save</strong>.
                </p>
              </div>
              <div
                id={`${titleId}-copilot-cli-panel`}
                role="tabpanel"
                aria-labelledby={`${titleId}-copilot-cli-tab`}
                hidden={method !== "copilot-cli"}
                tabIndex={0}
              >
                <strong>Add Excalidraw-MS to your assistant&apos;s config file.</strong>
                <div style={pathStyle}>
                  Windows — <code style={codeStyle}>%USERPROFILE%\.copilot\mcp-config.json</code>
                  <br />
                  macOS / Linux — <code style={codeStyle}>~/.copilot/mcp-config.json</code>
                </div>
                {urlState === "ready" && copilotCliSnippet && (
                  <>
                    <details open className="ai-config-disclosure">
                      <summary>View configuration</summary>
                      <pre style={preStyle} data-testid="ai-connect-cli-snippet">
                        {copilotCliSnippet}
                      </pre>
                    </details>
                    <div className="ai-actions" style={{ justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        className="ai-text-link"
                        onClick={() => void handleCopy("copilot-cli", copilotCliSnippet)}
                        data-testid="ai-connect-cli-copy"
                        aria-live="polite"
                      >
                        {copyLabel("copilot-cli", "Copy settings")}
                      </button>
                    </div>
                  </>
                )}
                {copyStates["copilot-cli"] === "error" && (
                  <p role="alert" style={warningStyle}>
                    Copy failed. Try again or select and copy the settings.
                  </p>
                )}
                <p style={mutedStyle}>
                  If <code style={codeStyle}>mcpServers</code> already lists other servers, add this
                  one alongside them rather than replacing what is there.
                </p>
              </div>
              <div className="ai-setup-signin">
                <strong>Enable and sign in to Excalidraw-MS.</strong>
                <p style={mutedStyle}>
                  Open your assistant&apos;s MCP server controls, enable{" "}
                  <code style={codeStyle}>excalidraw-ms</code>, and complete Microsoft sign-in when
                  prompted.
                </p>
              </div>
            </section>
          )}
          {step === 2 && <AiExamples />}
        </div>
        <p style={noteStyle} data-testid="ai-connect-note">
          Requires GitHub Copilot (CLI or app) with a Microsoft work account.
        </p>{" "}
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  // Sharing state is retained by the editor while this replaces its surface.
  zIndex: 10001,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  boxSizing: "border-box",
  outline: "none",
  width: "100%",
  maxWidth: 560,
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 24,
  fontFamily:
    "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  color: "var(--text-primary-color)",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 700,
};

const itemStyle: React.CSSProperties = {
  marginBottom: 18,
};

const pathStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: "var(--color-text-secondary)",
  lineHeight: 1.9,
};

const codeStyle: React.CSSProperties = {
  padding: "2px 6px",
  borderRadius: 6,
  background: "var(--input-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  fontSize: 12.5,
};

const preStyle: React.CSSProperties = {
  margin: "12px 0",
  padding: "12px 14px",
  borderRadius: 10,
  background: "var(--input-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  fontSize: 12.5,
  lineHeight: 1.5,
  maxHeight: 160,
  overflow: "auto",
  whiteSpace: "pre",
};

const mutedStyle: React.CSSProperties = {
  margin: "8px 0 0",
  color: "var(--color-text-secondary)",
  fontSize: 13,
  lineHeight: 1.5,
};

const warningStyle: React.CSSProperties = {
  margin: "12px 0 0",
  padding: "10px 12px",
  borderRadius: 10,
  background: "var(--exms-warning-soft)",
  border: "1px solid var(--exms-warning)",
  color: "var(--exms-warning-text)",
  fontSize: 13,
  lineHeight: 1.5,
};

const noteStyle: React.CSSProperties = {
  marginTop: 4,
  marginBottom: 0,
  paddingTop: 16,
  borderTop: "1px solid var(--dialog-border-color)",
  color: "var(--color-text-secondary)",
  fontSize: 14,
  lineHeight: 1.5,
};
